import { call, select } from "redux-saga/effects";

import { TripStatus, TripType } from "@mapmycustomers/shared";
import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import { Trip } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";

import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import MileageTrackingCardData from "@app/scene/dashboard/store/cards/mileageTracking/MileageTrackingCardData";
import { callApi } from "@app/store/api/callApi";
import { getCurrentUserId, getOrganizationId } from "@app/store/iam";
import { getTeams } from "@app/store/members";
import MileageTrackingCardConfiguration, {
  MileageTrackingAggregationType,
} from "@app/types/dashboard/cards/mileageTracking/MileageTrackingCardConfiguration";
import Scope from "@app/types/dashboard/Scope";
import AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import { doesWeekStartFromMonday, getLocalTimeZone } from "@app/util/dates";
import { parseApiDate } from "@app/util/parsers";

import { granularityByDateRange } from "./common";

interface DriveTimeAggregationItem {
  doc_count: number;
  key: number; // date in numeric format
  key_as_string: string; // date in string format
  rank: number;
  sum_duration: { value: number };
  without_duration: { value: number };
}

export function* fetchDriveTimeCardDataHelper(params: {
  callback: (data: MileageTrackingCardData) => void;
  configuration: MileageTrackingCardConfiguration;
  scope?: Scope;
}) {
  const { callback, configuration, scope } = params;
  const { criteria, dateRange, preferences } = configuration;
  const isTeamScope = !!scope?.teamId && !scope?.userId;
  const orgId: Organization["id"] = yield select(getOrganizationId);

  const granularity = granularityByDateRange[dateRange.range];
  const range = getDatesRange(dateRange.range, dateRange.subRange);

  const teams: Team[] = yield select(getTeams);
  let userIds = scope?.userId ? [scope.userId] : [];
  if (isTeamScope) {
    userIds = teams.find(({ id }) => id === scope?.teamId)?.users.map(({ id }) => id) ?? [];
  }

  const isUserScope = !scope?.teamId && !!scope?.userId;
  const currentUserId: User["id"] = yield select(getCurrentUserId);

  const filters = {
    status: TripStatus.COMPLETED,
    // when fetching for other users, only fetch business trips
    ...(isUserScope && currentUserId === scope?.userId ? {} : { type: TripType.BUSINESS }),
    $or: [
      { startTime: { $gte: range.startDate.toISOString(), $lte: range.endDate.toISOString() } },
      { endTime: { $gte: range.startDate.toISOString(), $lte: range.endDate.toISOString() } },
    ],
    userId: scope?.userId ? { [PlatformFilterOperator.CONTAINS]: userIds } : undefined,
  };

  const payload = {
    $aggs: {
      primaryGroup: {
        field: "startTime",
        interval: granularity,
        offset: granularity !== "week" || doesWeekStartFromMonday() ? undefined : "-1 day",
        timezone: getLocalTimeZone(),
      },
      sum: ["duration"],
    },
    $filters: filters,
    $limit: 0,
  };

  // TODO: fix type name
  const response: AggregatedListResponse<Trip, DriveTimeAggregationItem[]> = yield callApi(
    "fetchTrips",
    orgId,
    payload
  );

  const history: MileageTrackingCardData["history"] = response.aggregations.map((item) => ({
    date: parseApiDate(item.key),
    duration:
      criteria.aggregationType === MileageTrackingAggregationType.SUM
        ? (item.sum_duration.value ?? 0)
        : item.doc_count
          ? Math.round(item.sum_duration.value / item.doc_count)
          : 0,
  }));

  const result: MileageTrackingCardData = {
    duration: 0,
    history: preferences.showChart ? history : undefined,
  };

  result.duration = history?.reduce((sum, item) => sum + item.duration, 0) ?? 0;

  if (result.history && preferences.cumulative) {
    let durationSum = 0;
    result.history = result.history.map((item) => {
      durationSum += item.duration;
      return { ...item, duration: durationSum };
    });
  }

  if (preferences.showTrend) {
    // fetch again, but for the previous range
    const previousRange = getDatesRange(dateRange.range, dateRange.subRange, true);
    const payload = {
      $aggs: { sum: ["duration"] },
      $filters: {
        ...filters,
        $or: [
          {
            startTime: {
              $gte: previousRange.startDate.toISOString(),
              $lte: previousRange.endDate.toISOString(),
            },
          },
          {
            endTime: {
              $gte: previousRange.startDate.toISOString(),
              $lte: previousRange.endDate.toISOString(),
            },
          },
        ],
      },
      $limit: 0,
    };
    const response: AggregatedListResponse<Trip, DriveTimeAggregationItem> = yield callApi(
      "fetchTrips",
      orgId,
      payload
    );
    result.previousDuration = response.aggregations.sum_duration.value ?? 0;
  }

  yield call(callback, result);
}
